import type { FieldValues, Path } from 'react-hook-form';

import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';

import type {
  Option,
  SelectOptionValue,
} from '../../../../components/elements/SingleChoiceDropdown/SingleChoiceDropdown';
import { SingleChoiceDropdownWithLabel } from '../../../../components/elements/SingleChoiceDropdownWithLabel/SingleChoiceDropdownWithLabel';
import type { WidthProp } from '../../../../types/common-props';
import type { ControlledFieldProps } from '../FormField/FormField';

/** Base configuration props specific to the Select Input component. */
export interface BaseFormSelectInputProps<TFieldValues extends FieldValues> {
  /** Name identifier for the field within the form state. */
  name: Path<TFieldValues>;
  /** Text label displayed for the input. */
  label: string;
  /** Function to fetch/provide dropdown options, can be async or sync. */
  getOptions: (search: string) => Promise<Option<SelectOptionValue>[]> | Option<SelectOptionValue>[];
  /** Indicates if the field is mandatory. */
  isRequired?: boolean;
  /** Specifies the CSS width of the component. */
  width?: WidthProp;
  /** Placeholder text shown when no option is selected. */
  placeholder?: string;
  /** Data test ID for automated testing. */
  dataTestId?: string;
  /** Helper text displayed beneath the dropdown. */
  helperText?: string;
}

/** Combined props for the FormSelectInput component, including RHF control props. */
interface FormSelectInputProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>>
  extends BaseFormSelectInputProps<TFieldValues>,
    ControlledFieldProps<TFieldValues, TName> {
  // Includes field, errorMessage, isDisabled
}

/**
 * Renders a SingleChoiceDropdownWithLabel component integrated with React Hook Form,
 * receiving RHF state and handlers via the 'field' prop from a Controller wrapper.
 */
export function FormSelectInput<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  field, // RHF field object { value, onChange, onBlur, name, ref }
  errorMessage,
  isDisabled,
  label,
  getOptions,
  isRequired,
  width,
  placeholder,
  dataTestId,
  helperText,
}: FormSelectInputProps<TFieldValues, TName>) {
  /**
   * Adapter for the dropdown's onChange prop.
   * Receives the selected Option object (or null) and calls RHF's field.onChange
   * with the corresponding value (option.id or null).
   */
  const handleDropdownChange = (option: Option<SelectOptionValue> | null): void => {
    field.onChange(option ? option.id : '');
  };

  return (
    <SDSFlexbox direction="column" gap="no-design-4" fullWidth>
      <SingleChoiceDropdownWithLabel
        name={field.name}
        value={field.value}
        onChange={handleDropdownChange}
        label={label}
        getOptions={getOptions}
        isDisabled={isDisabled}
        isRequired={isRequired}
        errorMessage={errorMessage}
        width={width}
        inputPlaceholder={placeholder || label}
        dataTestId={dataTestId}
      />
      {helperText && <SDSText text={helperText} type="body-small" color="muted" />}
    </SDSFlexbox>
  );
}
